import Script from "next/script";
import { useRouter } from "next/router";
import { useState, useRef, useEffect } from "react";

import LeftSideNav from "@/components/left-side-nav/left-side-nav";
import Video from "@/components/video/video";
import CenterContent from "@/components/center-content/center-content";
import { Carousel } from "@/components/carousel/carousel";
import { SearchBar } from "@/components/search/search-bar";
import { useAppContext } from "@/context/appContext";

import JWPlayerConfig from "@/helpers/jw-helpers/jwplayer-config";
import updateUserActivity, { handleCourse } from "@/utils/update-user-activity";
import getUserActivity from "@/utils/get-user-activity";
import getDuration from "@/helpers/get-duration";
import sendFeedback from "@/utils/send-feedback";
import getAllUserLessons from "@/utils/get-all-user-lessons";
import FeedbackModal from "../modal/feedback";
import TextLessonPage from "../text-lesson-page/text-lesson-page";
import ShareModal from "../modal/share-modal";
import { hasUrlParameter } from "@/helpers/get-url-parameter";
import { setQueryParam } from "@/helpers/set-query-string";
import Breadcrumb from "../breadcrumb/Breadcrumb";
import Quiz from "../assessment/Quiz";
import {
  useSetLpUid,
  useCoursesStore,
  useAuthStore,
  useShowAlertNudge,
  useShowLoginModal,
  useSurveyStore,
} from "@/context/store";
import {
  fetchLearningPathLessons,
  fetchLearningPathProgress,
} from "@/utils/get-learning-path-details";
import useAuth from "@/utils/hooks/login-auth";
import CustomPopover from "../modal/custom-popover";
import getTotalCourseDuration from "@/helpers/get-total-course-duration";
import axiosHandler from "@/utils/axios-handler";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useAttempts } from "@/utils/hooks/checkAttempts";
import { getData } from "@/utils/academy-stack";
import useResponsive from "@/utils/hooks/Responsive-Screens";
import useUpdateLearningPath from "@/utils/update-learning-path";
import SurveyModal from "../survey-modal/survey-modal";
import usePartnerOverlay from "@/utils/hooks/partner-overlay";
import BlurOverlay from "@/components/blur-overlay/blur-overlay";
// import SkeletonLayout from "../loader/skeleton-loader";

export default function CourseDetails({
  pageData,
  videoEntry,
  videoConfig,
  isStandaloneCourse,
  videoData,
  isTrailerVideo,
}) {
  const {
    courseDetail,
    setCourseDetail,
    setCourseEntryUid,
    setLessonEntryUid,
    courseEntryUid,
    lessonEntryUid,
  } = useAppContext();

  const playerRef = useRef(null);
  const router = useRouter();
  const [lessons, setAllLessons] = useState([]);
  const [isLoadingAllLessons, setIsLoadingAllLessons] = useState(true);
  const { isAuthenticated, loggedInUser } = useAuth();
  const [isResponsive, setIsResponsive] = useState(false);
  const [breadcrumbTitle, setBreadcrumbTitle] = useState("");
  const [optimisticComplete, setOptimisticComplete] = useState(false);
  const [close, setClose] = useState(true);
  const [showFinalPersistent, setShowFinalPersistent] = useState(false);
  const [startCourse, setStartCourse] = useState(courseDetail.startCourse);
  const [cards, setCards] = useState([]);
  const { uid, isLearningPathEnrolled } = useSetLpUid();
  const { overlayState: showPartnerOverlay, handleLogout } = usePartnerOverlay({
    isPartnerContent: pageData?.is_partner_specific,
  });

  const { data: allLpLessons } = useQuery({
    queryKey: ["learningPathLessons", uid],
    queryFn: () => fetchLearningPathLessons(uid),
    enabled: Boolean(!!uid && loggedInUser),
    gcTime: 1000 * 60 * 5,
    retry: 1,
    onSuccess: () => {
      setIsLoadingAllLessons(false);
    },
    onError: () => {
      setIsLoadingAllLessons(false);
    },
  });
  const { data: allCourseProgress } = useQuery({
    queryKey: ["learningPathProgress", uid],
    queryFn: () => fetchLearningPathProgress(uid),
    enabled: Boolean(!!uid && loggedInUser),
    staleTime: 5 * 60 * 1000,
    retry: 1,
    onSuccess: () => {
      setIsLoadingAllLessons(false);
    },
    onError: () => {
      setIsLoadingAllLessons(false);
    },
  });
  const { courses, isAssessmentAvailable } = useCoursesStore();
  const {
    incrementAttempts,
    resetAttempts,
    getTotalAttempts,
    resetTotalAttempts,
  } = useAttempts();
  const setPreviousPath = useAuthStore((state) => state.setPreviousPath);
  const isLearningPath = router?.query?.learningPathway;
  const LpTitle = isLearningPath?.split("-").join(" ");
  const [isMounted, setIsMounted] = useState(false);
  const isLesson = router?.query?.lessons;
  const [isLoading, setIsLoading] = useState(true);
  const queryClient = useQueryClient();
  const { showAlertNudge, setShowAlertNudge } = useShowAlertNudge();
  const total_attempts = getTotalAttempts();
  const { showLoginModal, setShowLoginModal } = useShowLoginModal();
  const [persistantLogin, setPersistantLogin] = useState(false);
  const { isMobile, isTablet } = useResponsive();
  const isReady = router?.isReady;
  const [openEnroll, setOpenEnroll] = useState(false);
  const { showSurvey, setShowSurvey } = useSurveyStore();
  const { mutate, isPending } = useUpdateLearningPath(isLearningPath);

  useEffect(() => {
    setIsMounted(true);
  }, []);
  useEffect(() => {
    if (isLoading) {
      setIsLoading(false);
      return;
    }

    if (courses?.length === 0 && isLearningPath && !isLoading) {
      window.location.href = `/academy/learning-paths/${isLearningPath}`;
    }
  }, [courses, isLearningPath, isLoading]);

  useEffect(() => {
    if (
      !isAuthenticated &&
      total_attempts >= 4 &&
      isLesson &&
      !isStandaloneCourse
    ) {
      setShowFinalPersistent(true);
    } else if (
      !isAuthenticated &&
      total_attempts % 4 !== 0 &&
      total_attempts > 0 &&
      isLesson &&
      !isStandaloneCourse
    ) {
      setPersistantLogin(true);
    }
  }, []);
  useEffect(() => {
    if (loggedInUser) {
      resetAttempts();
      resetTotalAttempts();
    }
  }, [loggedInUser]);

  useEffect(() => {
    if (!uid) {
      return;
    }

    const API_PARAMS = {
      CONTENT_TYPE: "learning_pathways",
      FIELDS: {
        BASE: "learning_pathway_details.headline",
      },
    };

    // Update the fetchData function
    const fetchData = async () => {
      try {
        const queryParams = {
          contentType: API_PARAMS.CONTENT_TYPE,
          filters: [
            {
              key: "uid",
              value: uid,
            },
          ],
          fields: [
            {
              key: "only[BASE][]",
              value: API_PARAMS.FIELDS.BASE,
            },
          ],
        };

        const response = await getData(
          queryParams.contentType,
          "",
          queryParams.filters,
          queryParams.fields,
          null,
          null,
          null,
          null
        );

        setBreadcrumbTitle(response[0][0]?.learning_pathway_details?.headline);
      } catch (error) {
        console.error("Error fetching data:", error);
      }
    };

    fetchData();
  }, [uid]);

  useEffect(() => {
    setCourseEntryUid(pageData.uid);
    if (isStandaloneCourse) {
      setLessonEntryUid(videoEntry.lessonUid);
    } else {
      setLessonEntryUid(videoEntry?.uid);
    }
  }, [pageData.uid, videoEntry, isStandaloneCourse]);

  useEffect(() => {
    let vw = Math.max(
      document?.documentElement?.clientWidth || 0,
      window?.innerWidth || 0
    );
    if (vw <= 768 || vw < 576) {
      setIsResponsive(true);
      setClose(true);
    } else {
      setIsResponsive(false);
    }

    (async () => {
      const { courseData, lessonData, isCourse } = await getUserActivity({
        courseEntryUid: pageData.uid,
        lessonEntryUid: videoEntry.uid,
      });
      if (courseData?.status === 200) {
        setCourseDetail({
          ...courseDetail,
          courseData: courseData?.data,
          lessonData: lessonData?.data,
          isCourse: isCourse,
        });
        setStartCourse(courseData.data.progress.hasStarted);
        courseData.data.progress.hasStarted && setQueryParam("start", true);
        if (hasUrlParameter("start")) {
          setStartCourse(true);
        }
      } else {
        // Create course entry
        await updateUserActivity({
          courseEntryUid: pageData.uid,
          lessonEntryUid: videoEntry.uid,
        });
      }

      if (lessonData?.status === 200 && videoEntry?.uid) {
        setCourseDetail({
          ...courseDetail,
          courseData: courseData?.data,
          lessonData: lessonData?.data,
          isCourse: isCourse,
        });
      } else if (!isCourse) {
        // Create lesson entry
        await updateUserActivity({
          courseEntryUid: pageData.uid,
          lessonEntryUid: videoEntry.uid,
        });
      }
    })();

    const relatedCourses =
      pageData?.lesson_details?.related_courses?.reference ||
      pageData?.related_courses?.reference;

    let newCards = [];

    relatedCourses.forEach((item, i) =>
      newCards.push({
        ...item,
        url: item.is_standalone
          ? `${item?.url}`
          : `/academy/courses${item.url}`,
      })
    );

    setCards(newCards);
  }, []);
  const inCompleteCourses =
    allCourseProgress?.courses?.filter(
      (item) => item?.progress?.isCompleted === false
    ) || [];
  useEffect(() => {
    if (!pageData?.uid || !isAuthenticated) return;

    const fetchLessons = async () => {
      try {
        const res = await getAllUserLessons(pageData.uid);
        setAllLessons(res?.data?.lessons || []);
      } catch (error) {
        console.error("Error fetching lessons:", error);
        setAllLessons([]);
      } finally {
        setIsLoadingAllLessons(false);
      }
    };

    fetchLessons();
  }, [pageData?.uid, isAuthenticated]);

  const { data: CourseLessons } = useQuery({
    queryKey: ["courseLessonProgress", pageData.uid],
    queryFn: async () => {
      const lessonsOptions = {
        method: "GET",
        url: `${process.env.NEXT_PUBLIC_BASE_URL}/v1/users/courses/${pageData?.uid}/lessons`,
        withCredentials: true,
      };

      const lessons = await axiosHandler(lessonsOptions);
      return lessons;
    },
    enabled: !!pageData?.uid && !isLearningPath,
    gcTime: 3600000,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
  });

  const getLessons = () => {
    if (isLearningPath) {
      queryClient.invalidateQueries(["learningPathLessons", uid]);
    }
    getAllUserLessons(pageData.uid)
      .then((res) => {
        setAllLessons(res.data.lessons);
        setIsLoadingAllLessons(false);
      })
      .catch(() => {});
  };

  const handleStartCourse = async () => {
    await handleProgress({ key: "hasStarted", value: true }, true);

    const currentUrl = window.location.pathname;
    let startUrl = "";

    if (playerRef.current) {
      // eslint-disable-next-line no-undef
      jwplayer().play();
    }
    if (playerRef.current !== playerRef.previous) {
      // eslint-disable-next-line no-undef
      jwplayer().play();
    }
    setCourseDetail({ ...courseDetail, startCourse: true });

    // Determine if the user is on a course or a learning path since we have 2 different URL formats to navigate course
    const isCourse = currentUrl.includes("/academy/courses/");
    const isLearningPath = currentUrl.includes("/academy/learning-paths/");
    let pathnameId = Object.values(videoConfig).find(
      (obj) => obj.url === currentUrl
    );

    if (pathnameId) {
      startUrl = `${pathnameId.url}?start=true`;
      setQueryParam("start", "true");
    } else {
      if (
        isLearningPath &&
        videoConfig[0]?.url.includes("/academy/learning-paths/")
      ) {
        startUrl = `${videoConfig[0].url}?start=true`;
        setQueryParam("start", "true");
      } else if (
        isCourse &&
        videoConfig[0]?.url.includes("/academy/courses/")
      ) {
        startUrl = `${videoConfig[0].url}?start=true`;
        setQueryParam("start", "true");
      }
    }
    if (startUrl) {
      router.push(startUrl, undefined, { shallow: true });
    } else {
      console.error("No valid start URL found!");
    }
    if (isResponsive) {
      const textLesson = document.querySelector(".text-content-btns");
      if (textLesson) {
        textLesson.scrollIntoView();
      }
    }
    setQueryParam("start", "true");
    setStartCourse(true);
  };

  // Open Feedback modal
  const openFeedbackModal = () => {
    const modalTrigger = document.getElementById("feedback-modal-trigger");
    if (modalTrigger) {
      modalTrigger.click();
    }
  };

  // Handle Progress of course/lesson
  const handleProgress = async (progressData, updateCourseProgress) => {
    const progress = { [progressData.key]: progressData.value };
    const data = {
      courseEntryUid: pageData.uid,
      lessonEntryUid: isStandaloneCourse
        ? videoEntry.lessonUid
        : videoEntry.uid,
      lessonActivity: {
        progress: { ...progress },
      },
    };
    if (!isStandaloneCourse) {
      const dataLesson = await updateUserActivity(data, isStandaloneCourse);
      const courseProgressData =
        dataLesson?.data?.courseProgressTracking?.filter(
          (item) => item?.courseEntryUid === pageData?.uid
        );
      if (courseProgressData && courseProgressData[0]?.isNewlyCompleted) {
        setShowAlertNudge(true);
        setShowSurvey(true);
        return;
      }
    } else {
      await updateUserActivity(data, isStandaloneCourse);
    }

    if (updateCourseProgress) {
      await handleCourse({
        courseEntryUid: pageData.uid,
        courseActivity: { progress: { ...progress } },
      }).then((res) => {
        if (res?.data?.isNewlyCompleted && res?.data?.progress?.isCompleted) {
          if (!isStandaloneCourse || (isStandaloneCourse && isLearningPath)) {
            setOptimisticComplete(true);
            setShowAlertNudge(true);
            setShowSurvey(true);
          }
        }
      });
    }
  };

  // Handle Feedback Submit
  const handleFeedbackSubmit = async (data) => {
    const feedbackBox = document.querySelector("#user-feedback");

    let postData = {
      message: data,
      course: {
        title: pageData.title,
      },
    };

    let isCourse = true;
    let id = courseEntryUid;

    if (isStandaloneCourse) {
      postData = {
        message: data,
        course: {
          title: pageData.title,
        },
      };
      if (data?.length > 0) {
        await sendFeedback(postData, id, isCourse)
          .then((result) => {
            if (result.status === 200) {
              if (feedbackBox) {
                feedbackBox.value = "";
              }
              openFeedbackModal();
            }
          })
          .catch((error) => {});
      }
      return;
    }

    if (lessonEntryUid) {
      postData = {
        message: data,
        lesson: {
          title: videoEntry.title,
        },
      };

      isCourse = false;
      id = lessonEntryUid;
      if (data?.length > 0) {
        await sendFeedback(postData, id, isCourse)
          .then((result) => {
            if (result.status === 200) {
              if (feedbackBox) {
                feedbackBox.value = "";
              }
              openFeedbackModal();
            }
          })
          .catch((error) => {});
      }
      return;
    }

    if (data?.length > 0) {
      await sendFeedback(postData, id, isCourse)
        .then((result) => {
          if (result.status === 200) {
            // setShowFeedback(true);
            if (feedbackBox) {
              feedbackBox.value = "";
            }
            openFeedbackModal();
          }
        })
        .catch((error) => {});
    }
  };
  const handleSubmit = () => {
    mutate({ uid: uid, requestData: { isSubscribed: true } });
    if (allCourseProgress?.progress?.isCompleted ?? false) {
      router.push(`/learning-paths/${isLearningPath}/assessment`);
      //window.location.href = `/academy/learning-paths/${isLearningPath}/assessment`;
    } else {
      router.push(`/learning-paths/${isLearningPath}/assessment`);
    }
  };

  const hadleAssessment = () => {
    if (!isLearningPathEnrolled) {
      setOpenEnroll(true);
    } else {
      if (allCourseProgress?.progress?.isCompleted ?? false) {
        router.push(`/learning-paths/${isLearningPath}/assessment`);
        // window.location.href = `/academy/learning-paths/${isLearningPath}/assessment`;
      } else {
        router.push(`/learning-paths/${isLearningPath}/assessment`);
      }
    }
  };

  const handleLoginClick = (title) => {
    setShowLoginModal(false);
    resetTotalAttempts();
    // resetAttempts();
    window.location.href = "/academy/login";
  };
  const handlePersesistentLogin = () => {
    const currentPath = location.pathname;
    setPreviousPath(currentPath);
    window.location.href = "/academy/login";
  };

  let percentage = courseDetail?.courseData?.progress?.percentage;

  let approxTime = (percentage / 100) * videoEntry?.video_lesson?.[0]?.duration;

  videoData.approxTime = approxTime;

  // Add getTotalDuration function
  const getTotalDuration = (isStandalone) => {
    if (isStandalone) {
      return getDuration(pageData?.lesson_details?.video_lesson[0]?.duration);
    }
    if (videoEntry?.video_lesson?.[0]?.duration) {
      return getDuration(videoEntry.video_lesson[0].duration);
    } else {
      return videoEntry?.time_to_read || "0:00";
    }
  };

  useEffect(() => {
    if (!isAuthenticated) {
      setIsLoadingAllLessons(false);
    }
  }, [isAuthenticated]);

  const getLessonsProgress = () => {
    const incompleteLessons = pageData?.chapters[0]?.lessons?.lessons?.filter(
      (lesson) => {
        if (lesson.is_quiz) return false;
        // Find matching lesson from allLpLessons
        const matchingLesson = isLearningPath
          ? allLpLessons?.lessons?.find(
              (lpLesson) =>
                lpLesson.entryUid === lesson.uid &&
                !lpLesson.progress?.isCompleted
            )
          : CourseLessons?.data
            ? CourseLessons?.data?.lessons?.find(
                (lpLesson) =>
                  lpLesson.entryUid === lesson.uid &&
                  !lpLesson.progress?.isCompleted
              )
            : pageData?.chapters[0]?.lessons?.lessons[0];

        return matchingLesson;
      }
    );
    return incompleteLessons;
  };

  const incompleteLessons = getLessonsProgress();

  let pathname = router.asPath.replace(/\?.*$/, "");
  const currentCourseIndex = courses.findIndex(
    (course) => course.uid === pageData.uid
  );
  const nextCourseData = courses[currentCourseIndex + 1];
  const currentLessonName = pathname.split("/").pop();
  const currentIndex = pageData?.chapters?.[0]?.lessons?.lessons
    ? pageData.chapters[0].lessons.lessons.findIndex(
        (lesson) => lesson?.url?.split("/").pop() === currentLessonName
      )
    : 0;
  const nextLessonData =
    (pageData?.chapters?.[0]?.lessons?.lessons?.[currentIndex + 1] ||
      incompleteLessons?.[0]) ??
    null;
  const nextLessonIsBookmarked = lessons?.find(
    (lesson) => lesson.entryUid === nextLessonData?.uid
  )?.isBookmarked;

  const isLast = (() => {
    const lessons = pageData?.chapters?.[0]?.lessons?.lessons || [];
    const totalLessons = lessons.length;

    if (!totalLessons || currentIndex === -1) return false;

    const lastLesson = lessons[totalLessons - 1];
    const isLastQuiz = lastLesson?.is_quiz;

    if (isLastQuiz) {
      return currentIndex === totalLessons - 2;
    }

    return currentIndex === totalLessons - 1;
  })();
  const LearningPathAllData = courses?.flatMap((course) =>
    course?.chapters?.flatMap((chapter) => chapter?.lessons?.lessons || [])
  );
  const handleKeepLearning = () => {
    setShowAlertNudge(false);
    setShowSurvey(false);
    if (inCompleteCourses?.length === 0 && isLearningPath) {
      if (!isLearningPathEnrolled || !isAssessmentAvailable) {
        window.location.href = `/academy/learning-paths/${isLearningPath}`;
        return;
      }
      window.location.href = `/academy/learning-paths/${isLearningPath}/assessment`;
      return;
    }
    let url = isLearningPath
      ? nextCourseData?.url
        ? `/academy/learning-paths/${isLearningPath}${nextCourseData?.url}`
        : `/academy/learning-paths/${isLearningPath}`
      : "/academy/explore";
    window.location.href = url;
  };
  const handleCourseCompleteNudge = async () => {
    try {
      setShowAlertNudge(false);
      setShowSurvey(false);

      // Fetch fresh lesson data
      if (isLearningPath) {
        await queryClient.invalidateQueries(["learningPathLessons", uid]);
      } else {
        const freshLessons = await getAllUserLessons(pageData.uid);
        setAllLessons(freshLessons?.data?.lessons);
        setIsLoadingAllLessons(false);
      }

      // Update course detail state
      const { courseData, lessonData } = await getUserActivity({
        courseEntryUid: pageData.uid,
        lessonEntryUid: isStandaloneCourse
          ? videoEntry.lessonUid
          : videoEntry.uid,
      });

      if (courseData?.status === 200) {
        setCourseDetail((prevState) => ({
          ...prevState,
          courseData: courseData.data,
          lessonData: lessonData?.data,
        }));
      }
    } catch (error) {
      console.error("Error updating course data:", error);
    }
  };
  videoData.approxTime = approxTime;

  if (!isReady) return null;
  const shouldShowLpModal = isLearningPath && isLesson;

  if (showPartnerOverlay.show) {
    return (
      <BlurOverlay
        message={
          <>
            <h2 className="h3 mb-3">Partner-Only Content</h2>
            <p className="mb-2">
              This learning material is exclusively for{" "}
              <strong>Contentstack Partners</strong>. Please log in using your{" "}
              <strong>Partner account</strong> to access this content.
            </p>
            <p className="mb-0">
              If you’re not a partner, you can still explore other resources
              available to everyone in Contentstack Academy.
            </p>
          </>
        }
        buttons={[
          {
            id: "partner-login",
            title: "Log In as Partner",
            onClick: () => {
              handleLogout();
              window.location.href = showPartnerOverlay.LoginRedirectPath;
            },
          },
          {
            id: "academy-home",
            title: "Go to Academy Home",
            variant: "secondary",
            redirectPath: "/academy",
          },
        ]}
      />
    );
  }

  return (
    <>

      <Script
        src="https://cdn.jwplayer.com/libraries/b769H14u.js"
        onLoad={() =>
          JWPlayerConfig(
            playerRef.current,
            videoConfig,
            router,
            startCourse,
            videoData,
            handleProgress,
            isStandaloneCourse,
            getLessons,
            null,
            (isLast || incompleteLessons?.length === 1) &&
              !isStandaloneCourse &&
              loggedInUser
              ? true
              : false,
            isTrailerVideo?.length > 0 || (!loggedInUser && total_attempts >= 4)
              ? false
              : true,
            isTrailerVideo?.length > 0 ? true : false,
            setShowAlertNudge,
            incrementAttempts,
            isLearningPath,
            setShowSurvey
          )
        }
        lazyOnload
        async
      />

      {!loggedInUser && isMounted ? (
        shouldShowLpModal ? (
          <CustomPopover
            title="non-skippable persistent login LP"
            uid="bltec50257c744edebe"
            cancelTitle="go back"
            isModalOpen={true}
            customCancelHandler={() => {}}
            isNotSkippable={true}
            onSubmit={handlePersesistentLogin}
            message="You need to log in to access this content and track your progress."
            delay={0}
          />
        ) : (
          <>
            {" "}
            <CustomPopover
              title="persistent login"
              cancelTitle="cancel"
              isModalOpen={persistantLogin}
              uid="blt5629f18b8ea14708"
              customCancelHandler={() => {
                setPersistantLogin(false);
                resetAttempts();
              }}
              onSubmit={() => handleLoginClick("login")}
              submitTitle="Log In"
              message="Log in to keep track of your learning, resume courses anytime, and get personalized recommendations."
              delay={500}
            />
            <CustomPopover
              title="non-skippable persistent login"
              cancelTitle="go back"
              uid="blt4d7401bed9e10113"
              isModalOpen={showFinalPersistent}
              customCancelHandler={() => {
                return;
              }}
              isNotSkippable={true}
              onSubmit={() => handlePersesistentLogin()}
              message="You need to log in to access this content and track your progress."
              delay={0}
            />
            <CustomPopover
              title="learning path login"
              uid="bltfe9d96eeff3d5306"
              cancelTitle="go back"
              isModalOpen={showLoginModal}
              customCancelHandler={() => setShowLoginModal(false)}
              onSubmit={() => handleLoginClick("login")}
              submitTitle="Enroll Now"
              message="You need to log in to access this content and track your progress."
              delay={0}
            />
          </>
        )
      ) : null}

      {/** Search Bar */}
      {/* Replace the conditional rendering that causes hydration errors */}
      {!isLearningPath && (
        <div className="d-flex justify-content-start align-items-start section py-3 mt-2 mw-1920">
          <SearchBar
            placeholder={"Search within Academy ..."}
            buttonTitle={"Search"}
            customClass="ms-2"
          />
        </div>
      )}
      <CustomPopover
        title="Enroll to take Assessment"
        uid="bltf0c2fcf5ed89374d"
        cancelTitle="Cancel"
        isModalOpen={openEnroll}
        submitTitle={isPending ? "Enrolling..." : "Enroll"}
        onSubmit={() => {
          handleSubmit();
        }}
        message="Please enroll to the Learning Path to take Assessment"
        delay={500}
      />
      {isLearningPath && <Breadcrumb breadcrumbTitle={breadcrumbTitle} />}

      {/* Course Content */}
      <div className="amdy-container-wrapper">
        {/* LeftNav */}
        {isLearningPath && !videoEntry?.is_quiz && (
          <LeftSideNav
            data={courses}
            courseProgress={allCourseProgress?.courses}
            close={close}
            setClose={setClose}
            handleProgress={handleProgress}
            lessons={
              isAuthenticated && allLpLessons?.lessons?.length > 0
                ? allLpLessons?.lessons
                : LearningPathAllData
            }
            course={courseDetail.courseData}
            learningPathUrl={router?.query?.learningPathway}
            isStandalone={isStandaloneCourse}
            isAssessmentAvailable={isAssessmentAvailable}
            optimisticComplete={optimisticComplete}
            incrementAttempts={incrementAttempts}
            isLearningPathEnrolled={isLearningPathEnrolled}
            learningPathUid={uid}
            hadleAssessment={hadleAssessment}
            isCompleted={allCourseProgress?.progress?.isCompleted ?? false}
          />
        )}
        {!isStandaloneCourse && !videoEntry?.is_quiz && !isLearningPath && (
          <LeftSideNav
            data={pageData}
            close={close}
            setClose={setClose}
            handleProgress={handleProgress}
            isStandalone={isStandaloneCourse}
            lessons={lessons}
            incrementAttempts={incrementAttempts}
            course={courseDetail.courseData}
            isResponsive={isResponsive}
            isAssessmentAvailable={false}
            startCourse={startCourse}
          />
        )}

        {/* Video Content */}
        <main
          className={`course-content-wrapper ${
            (!isStandaloneCourse && !close && !isLearningPath) ||
            (!isStandaloneCourse && !close && isLearningPath) ||
            (isStandaloneCourse && isLearningPath && !close)
              ? "content-opacity"
              : ""
          } ${startCourse || !close ? "player-width" : ""} ${
            startCourse && !close ? "player-expanded" : ""
          } mx-auto`}
        >
          {!close && isMobile && (
            <div
              onClick={() => setClose(true)}
              style={{
                position: "fixed",
                top: "155px",
                left: 0,
                width: "100%",
                height: "calc(140vh - 115px)",
                // backgroundColor: "rgba(0, 0, 0, 0.6)",
                zIndex: 99,
                transition: "background-color 0.3s ease",
              }}
            />
          )}

          {loggedInUser &&
            (!isStandaloneCourse || (isStandaloneCourse && isLearningPath)) && (
              <CustomPopover
                title="course completion"
                customCancelHandler={handleCourseCompleteNudge}
                uid="bltaac23c30f04e192a"
                cancelTitle="Cancel"
                isModalOpen={showAlertNudge}
                onSubmit={handleKeepLearning}
                submitTitle="Keep Learning"
                dynamicMessage={true}
                message={`Congrats, ${loggedInUser?.firstName}! You completed the ${pageData?.title}. Keep up the good work.`}
                delay={500}
              />
            )}
          {((!videoEntry?.text_lesson && !videoEntry?.is_quiz) ||
            (!videoEntry?.text_lesson && isStandaloneCourse)) && (
            <Video
              data={pageData}
              transcriptions_captions={videoEntry?.transcriptions_captions}
              close={close}
              setClose={setClose}
              playerRef={playerRef}
              videoEntry={videoEntry}
              videoConfig={videoConfig}
              videoData={videoData}
              isStandaloneCourse={isStandaloneCourse}
              lpTitle={LpTitle}
              isLesson={isLesson}
              isLast={isLast}
              inCompletedLessons={incompleteLessons}
              isCourseDetailsPage={isStandaloneCourse && !isLearningPath}
            />
          )}

          {videoEntry?.text_lesson &&
            !videoEntry?.is_quiz &&
            !isStandaloneCourse && (
              <TextLessonPage
                title={pageData.title}
                incrementAttempts={incrementAttempts}
                videoEntry={videoEntry}
                content={videoEntry.text_lesson}
                hasCompleted={courseDetail?.courseData?.progress?.isCompleted}
                courseId={courseDetail?.courseData?.entryUid}
                videoConfig={videoConfig}
                setClose={setClose}
                close={close}
                handleFeedbackSubmit={handleFeedbackSubmit}
                data={pageData}
                href={nextLessonData?.url}
                isStandaloneCourse={isStandaloneCourse}
                handleStartCourse={handleStartCourse}
                getLessons={getLessons}
                startCourse={startCourse}
                inCompletedLessons={incompleteLessons}
                courseProgress={courseDetail?.lessonData}
                percentage={percentage}
                isResponsive={isResponsive}
                duration={getTotalDuration()}
                isLast={isLast}
                isLearningPath={isLearningPath}
                nextLessonData={nextLessonData}
                nextLessonIsBookmarked={nextLessonIsBookmarked}
              />
            )}
          {videoEntry?.is_quiz && (
            <Quiz
              courseUid={courseEntryUid}
              lessonUid={lessonEntryUid}
              videoEntry={videoEntry}
              data={pageData}
              isStandaloneCourse={isStandaloneCourse}
              videoConfig={videoConfig}
              assessmentName={
                pageData?.learning_pathway_details?.headline || pageData?.title
              }
              assessment_test={{
                ...videoEntry?.quiz,
              }}
              courseUrl={pageData?.url}
              progress={courseDetail?.lessonData}
            />
          )}

          {/* Center Content */}
          {!isLesson ? (
            <CenterContent
              pageData={pageData}
              startCourse={startCourse}
              isResponsive={isResponsive}
              videoEntry={videoEntry}
              courseProgress={courseDetail?.courseData}
              handleFeedbackSubmit={handleFeedbackSubmit}
              isStandaloneCourse={isStandaloneCourse}
              duration={
                isStandaloneCourse
                  ? pageData?.lesson_details?.video_lesson[0]?.duration
                  : getTotalCourseDuration(pageData)
              }
              handleStartCourse={handleStartCourse}
              isLearningPath={isLearningPath}
              inCompletedLessons={incompleteLessons}
              setClose={setClose}
              close={close}
            />
          ) : (
            !videoEntry?.text_lesson &&
            !videoEntry?.is_quiz && (
              <CenterContent
                pageData={pageData}
                startCourse={startCourse}
                videoEntry={videoEntry}
                videoConfig={videoConfig}
                isResponsive={isResponsive}
                courseProgress={courseDetail?.lessonData}
                handleFeedbackSubmit={handleFeedbackSubmit}
                isStandaloneCourse={isStandaloneCourse}
                duration={getTotalDuration(isStandaloneCourse)}
                handleStartCourse={handleStartCourse}
                isLearningPath={isLearningPath}
              />
            )
          )}

          {!isLearningPath &&
            !videoEntry?.is_quiz &&
            cards &&
            cards.length > 0 && (
              <section className="position-relative section related-courses mt-5 mb-40">
                <Carousel
                  carouselTitle="Related Content"
                  carouselLink={{
                    title: "View all content",
                    href: "/academy/explore",
                  }}
                  cards={cards}
                  slidesPerView={3}
                  fromCourse
                />
              </section>
            )}
        </main>
      </div>
      <a
        href="#"
        className="link-basic fw-semi-bold d-none"
        data-bs-toggle="modal"
        data-bs-target="#feedback-modal"
        id="feedback-modal-trigger"
      >
        Open Feedback Modal
      </a>

      {showSurvey && <SurveyModal url={`/academy/courses${pageData?.url}`} />}
      <FeedbackModal />
      <ShareModal />
    </>
  );
}
